import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { ManualeButton } from "@/components/ManualeButton";
import { AppIcon } from "@/components/AppIcon";
import { ArrowRight, ArrowLeft, Building2 } from "lucide-react";

export default function GestioneAgenzia() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/apps").then(({ data }) => setApps(data)).catch(() => setApps([])).finally(() => setLoading(false));
  }, []);

  const gaApps = apps.filter((a) => a.section === "gestione_agenzia");

  const openApp = async (app) => {
    try { await api.post("/logs/access", { app_id: app.app_id }); } catch { /* noop */ }
    if (app.app_type === "native") navigate(app.route);
    else if (app.app_type === "integrated") navigate(`/app/${app.app_id}`);
    else window.open(app.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="ga-back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Torna alla home
        </button>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="h-4 w-4 text-indigo-600" />
              <p className="text-xs uppercase tracking-[0.2em] text-indigo-600 font-semibold">Gestione Agenzia</p>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Gestione interna dell'agenzia</h1>
            <p className="text-slate-500 mt-2">Personale, adempimenti e attività amministrative dell'agenzia.</p>
          </div>
          <ManualeButton route="/manuale/gestione-agenzia" />
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-44 rounded-xl bg-white border border-slate-200 animate-pulse" />)}
          </div>
        ) : gaApps.length === 0 ? (
          <div data-testid="ga-empty" className="text-center py-24 rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500">
            Nessuna app di gestione disponibile al momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ga-apps-grid">
            {gaApps.map((app, i) => (
              <motion.button
                key={app.app_id}
                data-testid={`ga-app-tile-${app.app_id}`}
                onClick={() => openApp(app)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="group relative text-left rounded-xl bg-white border border-slate-200 p-6 shadow-sm hover:border-indigo-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-300"
              >
                <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${app.color}1A`, color: app.color }}>
                  <AppIcon name={app.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-heading text-lg font-medium text-slate-900 mb-1.5">{app.name}</h3>
                <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem]">{app.description}</p>
                <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-indigo-500 transition-colors">
                  Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
